import type { AiCallTotals } from "@pax/api-contract/ai/calls/v1alpha1";
import {
  type Dimension,
  dimensionGroups,
  dimensions,
  faultNames,
  type Keep,
  pinned,
} from "./dimensions";

export type Totals = AiCallTotals;
export type TokenKind = keyof Totals["tokens"];

/* ── Numbers ────────────────────────────────────────────────────────── */

export const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

export function usd(value: number): string {
  if (value >= 10_000) return `$${(value / 1000).toFixed(1)}k`;
  if (value >= 100) return `$${Math.round(value).toLocaleString()}`;
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value === 0) return "$0";
  if (value >= 0.001) return `${(value * 100).toFixed(value >= 0.1 ? 0 : 1)}¢`;
  return `${(value * 100).toLocaleString("en-US", { maximumSignificantDigits: 2 })}¢`;
}

export function compact(value: number): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e4) return `${Math.round(value / 1e3)}k`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
  return String(Math.round(value));
}

export function percent(value: number): string {
  const points = value * 100;
  return `${points < 10 && points > 0 ? points.toFixed(1) : Math.round(points)}%`;
}

export function seconds(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/* ── Totals ─────────────────────────────────────────────────────────── */

export const tokenKinds: Record<TokenKind, string> = {
  uncachedInput: "Input",
  cacheRead: "Cache read",
  cacheWrite: "Cache write",
  reasoning: "Reasoning",
  output: "Output",
};
export const tokenKindIds = Object.keys(tokenKinds) as TokenKind[];

const sum = (split: Record<TokenKind, number>) =>
  tokenKindIds.reduce((total, kind) => total + split[kind], 0);

/** Token-kind rows carry tokens but no calls, and still have something to show. */
export const hasCalls = (totals: Totals) => totals.calls + sum(totals.tokens) > 0;

const promptTokens = ({ tokens }: Totals) =>
  tokens.uncachedInput + tokens.cacheRead + tokens.cacheWrite;

const zeroSplit = () => ({
  uncachedInput: 0,
  cacheRead: 0,
  cacheWrite: 0,
  reasoning: 0,
  output: 0,
});

const zeroSpend = () => ({ ...zeroSplit(), tools: 0 });

export function emptyTotals(): Totals {
  return {
    calls: 0,
    failed: 0,
    malformed: 0,
    toolCalls: 0,
    tokens: zeroSplit(),
    costUsd: 0,
    spendUsd: zeroSpend(),
    unknownCostCalls: 0,
    unpricedCalls: 0,
    firstTokenP50Ms: null,
    firstTokenP95Ms: null,
    outputTokensPerSecond: null,
    errorTypes: {},
    faults: {},
  };
}

const addSplits = (left: Totals["tokens"], right: Totals["tokens"]) =>
  Object.fromEntries(
    tokenKindIds.map(kind => [kind, left[kind] + right[kind]]),
  ) as Totals["tokens"];

const addCounts = (left: Record<string, number>, right: Record<string, number>) => {
  const counts = { ...left };
  for (const [key, count] of Object.entries(right)) counts[key] = (counts[key] ?? 0) + count;
  return counts;
};

const weighted = (
  left: number | null,
  leftWeight: number,
  right: number | null,
  rightWeight: number,
) =>
  left === null
    ? right
    : right === null || leftWeight + rightWeight === 0
      ? left
      : (left * leftWeight + right * rightWeight) / (leftWeight + rightWeight);

const finished = (totals: Totals) => totals.calls - totals.failed;
const outputTokens = ({ tokens }: Totals) => tokens.output + tokens.reasoning;

/**
 * Two sets of calls together. Counts add exactly; percentiles and speed can't be recombined from
 * totals, so they're averaged, weighted by the calls or tokens behind them.
 */
function addTotals(left: Totals, right: Totals): Totals {
  return {
    calls: left.calls + right.calls,
    failed: left.failed + right.failed,
    malformed: left.malformed + right.malformed,
    toolCalls: left.toolCalls + right.toolCalls,
    tokens: addSplits(left.tokens, right.tokens),
    costUsd: left.costUsd + right.costUsd,
    spendUsd: {
      ...addSplits(left.spendUsd, right.spendUsd),
      tools: left.spendUsd.tools + right.spendUsd.tools,
    },
    unknownCostCalls: left.unknownCostCalls + right.unknownCostCalls,
    unpricedCalls: left.unpricedCalls + right.unpricedCalls,
    firstTokenP50Ms: weighted(
      left.firstTokenP50Ms,
      finished(left),
      right.firstTokenP50Ms,
      finished(right),
    ),
    firstTokenP95Ms: weighted(
      left.firstTokenP95Ms,
      finished(left),
      right.firstTokenP95Ms,
      finished(right),
    ),
    outputTokensPerSecond: weighted(
      left.outputTokensPerSecond,
      outputTokens(left),
      right.outputTokensPerSecond,
      outputTokens(right),
    ),
    errorTypes: addCounts(left.errorTypes, right.errorTypes),
    faults: addCounts(left.faults, right.faults),
  };
}

export const addAll = (all: Totals[]) => all.reduce(addTotals, emptyTotals());

export function onlyKind(totals: Totals, kind: TokenKind): Totals {
  const only = emptyTotals();
  only.tokens[kind] = totals.tokens[kind];
  only.spendUsd[kind] = totals.spendUsd[kind];
  only.costUsd = totals.spendUsd[kind];
  return only;
}

/** The key a count record holds most of, for a row's quiet line. */
export const mostOf = (counts: Record<string, number>) =>
  Object.entries(counts).sort(([, left], [, right]) => right - left)[0]?.[0];

/* ── Measures ───────────────────────────────────────────────────────── */

export type Metric =
  | "spend"
  | "calls"
  | "tokens"
  | "cacheHit"
  | "firstToken"
  | "speed"
  | "malformed"
  | "failures";

export interface MetricInfo {
  label: string;
  column: string;
  /** Exactly what's counted, for measures that could mean more than one thing. */
  definition: string;
  /** Adds up across calls, so it stacks; anything else is an average and draws as a line. */
  additive: boolean;
  value: (totals: Totals, slowest: boolean) => number | null;
  format: (value: number) => string;
  /** How a change against the window before reads. */
  change: [more: string, less: string];
  /** A rate changes by percentage points rather than by a share of itself. */
  inPoints?: boolean;
}

export const metrics: Record<Metric, MetricInfo> = {
  spend: {
    label: "Spend",
    column: "Spend",
    definition:
      "What Pax was billed: the router's bill for routed calls, list prices for direct ones. A call whose usage went unreported, or that Pax has no price for, adds nothing, and is counted on its own.",
    additive: true,
    value: totals => totals.costUsd,
    format: usd,
    change: ["more", "less"],
  },
  calls: {
    label: "Calls",
    column: "Calls",
    definition:
      "Requests Pax sent to a model, failed ones included. Each retry counts as its own call.",
    additive: true,
    value: totals => totals.calls,
    format: compact,
    change: ["more", "fewer"],
  },
  tokens: {
    label: "Tokens",
    column: "Tokens",
    definition:
      "Every token billed: prompt tokens, whether read from the cache, written to it or neither, plus output tokens, reasoning included.",
    additive: true,
    value: totals => sum(totals.tokens),
    format: compact,
    change: ["more", "fewer"],
  },
  cacheHit: {
    label: "Cache hit rate",
    column: "Cache hit",
    definition:
      "Prompt tokens read from the provider's cache, as a share of all prompt tokens. Tokens written to the cache count as misses.",
    additive: false,
    value: totals => {
      const prompt = promptTokens(totals);
      return prompt > 0 ? totals.tokens.cacheRead / prompt : null;
    },
    format: percent,
    change: ["higher", "lower"],
    inPoints: true,
  },
  firstToken: {
    label: "Time to first token",
    column: "First token",
    definition:
      "From Pax sending the request to the first streamed token of any kind: reasoning, a reasoning summary, text or tool arguments. Reasoning that streams nothing counts as waiting.",
    additive: false,
    value: (totals, slowest) => (slowest ? totals.firstTokenP95Ms : totals.firstTokenP50Ms),
    format: seconds,
    change: ["slower", "faster"],
  },
  speed: {
    label: "Output speed",
    column: "Speed",
    definition:
      "Output tokens per second, from the first streamed token to the last. The wait before the first token doesn't count, and neither does reasoning that streams nothing.",
    additive: false,
    value: totals => totals.outputTokensPerSecond,
    format: value => `${Math.round(value)}/s`,
    change: ["faster", "slower"],
  },
  malformed: {
    label: "Malformed rate",
    column: "Malformed",
    definition:
      "Of the calls that finished, the share whose output Pax couldn't use: a tool call that isn't valid JSON, names a tool it doesn't have or breaks the tool's schema, or structured output that breaks its schema.",
    additive: false,
    value: totals => (finished(totals) > 0 ? totals.malformed / finished(totals) : null),
    format: percent,
    change: ["higher", "lower"],
    inPoints: true,
  },
  failures: {
    label: "Failure rate",
    column: "Failed",
    definition:
      "Calls that ended without a response: an error from the provider or router, a dropped connection, a timeout or a cancellation. Malformed calls aren't counted here.",
    additive: false,
    value: totals => (totals.calls > 0 ? totals.failed / totals.calls : null),
    format: percent,
    change: ["higher", "lower"],
    inPoints: true,
  },
};

export const metricIds = Object.keys(metrics) as Metric[];

/** Only spend and tokens break down by token kind. */
export const splitsByTokenKind = (view: View) => view === "spend" || view === "tokens";

/* ── Views and their columns ────────────────────────────────────────── */

/** What the page is about: every measure at a glance, or one measure and what it's made of. */
export type View = "overview" | Metric;

/** One column of the table. Any column can be charted. */
export interface Column extends MetricInfo {
  id: string;
  /** Kept on narrow screens. */
  narrow?: boolean;
  /** Still means something when the rows are token kinds. */
  perTokenKind?: boolean;
}

export const measure = (metric: Metric, extra: Partial<Column> = {}): Column => ({
  id: metric,
  ...metrics[metric],
  ...extra,
});

const tokenNoun = (kind: TokenKind) =>
  kind === "uncachedInput" ? "uncached input" : tokenKinds[kind].toLowerCase();

const spendOn = (kind: TokenKind, narrow = false): Column => ({
  id: `spend.${kind}`,
  label: `${tokenKinds[kind]} spend`,
  column: tokenKinds[kind],
  definition: `The list cost of every ${tokenNoun(kind)} token Pax priced.${kind === "reasoning" ? " Reasoning is billed as output, at the output price." : ""}`,
  additive: true,
  value: totals => totals.spendUsd[kind],
  format: usd,
  change: ["more", "less"],
  narrow,
});

const tokensOf = (kind: TokenKind, narrow = false): Column => ({
  id: `tokens.${kind}`,
  label: `${tokenKinds[kind]} tokens`,
  column: tokenKinds[kind],
  definition: `Every ${tokenNoun(kind)} token billed.`,
  additive: true,
  value: totals => totals.tokens[kind],
  format: compact,
  change: ["more", "fewer"],
  narrow,
});

const count = (
  id: string,
  label: string,
  column: string,
  definition: string,
  value: (totals: Totals) => number,
  narrow = false,
): Column => ({
  id,
  label,
  column,
  definition,
  additive: true,
  value,
  format: compact,
  change: ["more", "fewer"],
  narrow,
});

const errorCount = (type: string, column: string, definition: string, narrow = false) =>
  count(
    `errors.${type}`,
    `${column} calls`,
    column,
    definition,
    t => t.errorTypes[type] ?? 0,
    narrow,
  );

const faultCount = (fault: string, definition: string, narrow = false) =>
  count(
    `faults.${fault}`,
    `${faultNames[fault]} failures`,
    faultNames[fault] ?? fault,
    definition,
    totals => totals.faults[fault] ?? 0,
    narrow,
  );

const perCall = (
  id: string,
  label: string,
  definition: string,
  value: (totals: Totals) => number,
  format: (value: number) => string,
): Column => ({
  id,
  label,
  column: "Per call",
  definition,
  additive: false,
  value: totals => (totals.calls > 0 ? value(totals) / totals.calls : null),
  format,
  change: ["more", "less"],
  narrow: true,
});

export const views: Record<View, { label: string; columns: Column[] }> = {
  overview: {
    label: "Overview",
    columns: metricIds.map(metric =>
      measure(metric, {
        narrow: ["spend", "firstToken", "malformed", "failures"].includes(metric),
        perTokenKind: splitsByTokenKind(metric),
      }),
    ),
  },
  spend: {
    label: metrics.spend.label,
    columns: [
      measure("spend", { narrow: true, perTokenKind: true }),
      spendOn("uncachedInput"),
      spendOn("cacheRead", true),
      spendOn("cacheWrite", true),
      spendOn("reasoning"),
      spendOn("output"),
      {
        id: "spend.tools",
        label: "Tool spend",
        column: "Tools",
        definition:
          "The list cost of tools the provider runs and bills per use, such as web search, and of requests billed per request.",
        additive: true,
        value: totals => totals.spendUsd.tools,
        format: usd,
        change: ["more", "less"],
      },
      count(
        "spend.unknownCost",
        "Calls of unknown cost",
        "Unknown cost",
        "Calls whose response reported no usage, so what they cost can't be known. Spend leaves them out.",
        totals => totals.unknownCostCalls,
      ),
      count(
        "spend.unpriced",
        "Unpriced calls",
        "Unpriced",
        "Calls with usage Pax has no price for: a gap in Pax's price data, which spend leaves out.",
        totals => totals.unpricedCalls,
      ),
      perCall(
        "spend.perCall",
        "Spend per call",
        "What Pax was billed, divided by every call, failed ones included.",
        totals => totals.costUsd,
        usd,
      ),
    ],
  },
  calls: {
    label: metrics.calls.label,
    columns: [
      measure("calls", { narrow: true }),
      count(
        "calls.tools",
        "Tool calls",
        "Tool calls",
        "Tool calls the replies asked for, malformed ones included.",
        totals => totals.toolCalls,
      ),
      count(
        "calls.malformed",
        "Malformed calls",
        "Malformed",
        "Calls that finished with tool calls or structured output Pax couldn't use.",
        totals => totals.malformed,
        true,
      ),
      count(
        "calls.failed",
        "Failed calls",
        "Failed",
        "Calls that ended without a response.",
        totals => totals.failed,
        true,
      ),
    ],
  },
  tokens: {
    label: metrics.tokens.label,
    columns: [
      measure("tokens", { narrow: true, perTokenKind: true }),
      tokensOf("uncachedInput"),
      tokensOf("cacheRead", true),
      tokensOf("cacheWrite"),
      tokensOf("reasoning"),
      tokensOf("output", true),
      perCall(
        "tokens.perCall",
        "Tokens per call",
        "Every token billed, divided by every call, failed ones included.",
        totals => sum(totals.tokens),
        compact,
      ),
    ],
  },
  cacheHit: {
    label: metrics.cacheHit.label,
    columns: [
      measure("cacheHit", { narrow: true }),
      { ...tokensOf("cacheRead", true), column: "Read" },
      { ...tokensOf("cacheWrite"), column: "Written" },
      { ...tokensOf("uncachedInput", true), column: "Uncached" },
      perCall(
        "cacheHit.prompt",
        "Prompt per call",
        "Prompt tokens, read from the cache, written to it or neither, divided by every call.",
        promptTokens,
        compact,
      ),
    ],
  },
  firstToken: {
    label: metrics.firstToken.label,
    columns: [
      measure("firstToken", { column: "Median", narrow: true }),
      {
        ...measure("firstToken"),
        id: "firstToken.p95",
        label: "Time to first token at the 95th percentile",
        column: "p95",
        value: totals => totals.firstTokenP95Ms,
        narrow: true,
      },
      measure("speed", { narrow: true }),
      measure("calls"),
    ],
  },
  speed: {
    label: metrics.speed.label,
    columns: [
      measure("speed", { narrow: true }),
      count(
        "speed.output",
        "Output tokens",
        "Output",
        "Output tokens, reasoning included, that the speed is measured over.",
        outputTokens,
        true,
      ),
      measure("firstToken", { narrow: true }),
      measure("calls"),
    ],
  },
  malformed: {
    label: metrics.malformed.label,
    columns: [
      measure("malformed", { narrow: true }),
      count(
        "malformed.calls",
        "Malformed calls",
        "Malformed",
        "Calls that finished with tool calls or structured output Pax couldn't use.",
        totals => totals.malformed,
        true,
      ),
      errorCount(
        "invalid_json",
        "Invalid JSON",
        "Tool arguments or structured output that don't parse as JSON, often cut off partway.",
      ),
      errorCount(
        "schema_violation",
        "Schema",
        "Output that parses but breaks its schema: a missing argument, a wrong type or a value it doesn't allow.",
        true,
      ),
      errorCount("unknown_tool", "Unknown tool", "Tool calls naming a tool the call wasn't given."),
    ],
  },
  failures: {
    label: metrics.failures.label,
    columns: [
      measure("failures", { narrow: true }),
      count(
        "failures.calls",
        "Failed calls",
        "Failed",
        "Calls that ended without a response.",
        totals => totals.failed,
      ),
      faultCount(
        "provider",
        "Overloaded, server errors, timeouts, dropped connections and streams that stopped partway.",
        true,
      ),
      faultCount(
        "request",
        "Rate limits on Pax's own quota, prompts past the context window and requests the provider rejected.",
        true,
      ),
      faultCount("cancelled", "Calls Pax stopped itself, because the agent's turn was cancelled."),
    ],
  },
};

export const isView = (value: unknown): value is View =>
  typeof value === "string" && value in views;

/* ── Splits ─────────────────────────────────────────────────────────── */

export type Split = Dimension | "tokenKind" | "none";

export const isSplit = (value: unknown): value is Split =>
  value === "tokenKind" || value === "none" || (typeof value === "string" && value in dimensions);

export const splitDimension = (split: Split): Dimension | undefined =>
  split === "none" || split === "tokenKind" ? undefined : split;

/** What the split picker shows, after a grey "grouped by". */
export const splitLabel = (split: Split) =>
  split === "none" ? "nothing" : split === "tokenKind" ? "token kind" : dimensions[split].label;

export const splitPhrase = (split: Split) =>
  split === "none" ? "in total" : `grouped by ${splitLabel(split)}`;

/**
 * The grouping picker's choices: every dimension the filter hasn't pinned to one value, then
 * token kind where it applies, then nothing.
 */
export function splitGroups(view: View, split: Split, keep: Keep) {
  return [
    ...dimensionGroups.map(group =>
      group.dimensions
        .filter(dimension => dimension === split || !pinned(dimension, keep))
        .map(dimension => ({
          id: dimension,
          label: splitLabel(dimension),
          selected: dimension === split,
        })),
    ),
    [
      ...(splitsByTokenKind(view)
        ? [{ id: "tokenKind", label: "token kind", selected: split === "tokenKind" }]
        : []),
      { id: "none", label: "nothing", selected: split === "none" },
    ],
  ].filter(options => options.length > 0);
}

/** Where the page's grouping goes when a filter pins the one it had to a single value. */
export function unpinnedSplit(split: Split, keep: Keep): Split {
  const current = splitDimension(split);
  if (!current || !pinned(current, keep)) return split;
  return (
    (["model", "transport", "host", "agent"] as Dimension[]).find(d => !pinned(d, keep)) ?? "none"
  );
}

/** What each measure is best broken down by on a profile, in order of preference. */
const profileSplits: Record<Metric, Split[]> = {
  spend: ["tokenKind"],
  calls: ["agent", "model", "transport"],
  tokens: ["gameKind", "agent", "model", "transport", "host"],
  cacheHit: ["transport", "host", "model"],
  firstToken: ["host", "transport", "model"],
  speed: ["host", "transport", "model"],
  malformed: ["host", "model", "transport"],
  failures: ["host", "transport", "model"],
};

/** The first of a measure's preferred groupings the filter hasn't pinned. */
export function bestSplit(metric: Metric, keep: Keep): Split {
  return (
    profileSplits[metric].find(candidate =>
      candidate === "tokenKind"
        ? splitsByTokenKind(metric)
        : candidate === "none" || !pinned(candidate, keep),
    ) ?? "none"
  );
}

/** The measures a profile opens on, one chart each. */
export const profileCharts: { metric: Metric; slowest: boolean }[] = [
  { metric: "spend", slowest: false },
  { metric: "calls", slowest: false },
  { metric: "tokens", slowest: false },
  { metric: "cacheHit", slowest: false },
  { metric: "firstToken", slowest: false },
  { metric: "firstToken", slowest: true },
  { metric: "speed", slowest: false },
  { metric: "malformed", slowest: false },
  { metric: "failures", slowest: false },
];

export interface ChartSpec {
  id: number;
  metric: Metric;
  slowest: boolean;
  split: Split;
}
